/**
 * Portal-Nachrichten für Eltern/Schüler.
 *
 * Ähnlich wie die Admin-Nachrichtenzentrale, aber:
 *   - Nutzt /portal/messages/* Endpoints (Portal-JWT, nicht Admin-Token)
 *   - Directory ist auf Lehrer:innen der eigenen Kinder + Mandanten-Staff beschränkt
 *   - Kein Gruppen-Chat im MVP (nur 1:1), kein Archivieren, kein Löschen fremder Nachrichten
 */
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { MessageSquare, Plus, Send, Paperclip, X, Search, Loader2, Trash2, Download, Users as UsersIcon, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import PortalLayout from './PortalLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import api from '@/lib/axios';
import { formatRelativeDateTime } from '@/lib/utils';
import MessageDeliveryStatus from '@/components/messages/MessageDeliveryStatus';
import { matchesFreeText } from '@/components/search/SearchBuilder';

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
}

function linkify(text, isOwn) {
  const parts = String(text).split(/(https?:\/\/[^\s]+)/g);
  return parts.map((p, i) => (/^https?:\/\//.test(p) ? (
    <a key={i} href={p} target="_blank" rel="noopener noreferrer"
       className={`underline font-medium break-all ${isOwn ? 'text-brand-red/70' : 'text-brand-red'}`}>{p}</a>
  ) : p));
}

function formatMessageTime(iso) {
  return formatRelativeDateTime(iso);
}

export default function PortalMessagesPage() {
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [myUserId, setMyUserId] = useState(null); // bridge user id
  const scrollRef = useRef(null);

  const reloadConvs = useCallback(async () => {
    setLoadingConvs(true);
    try {
      const r = await api.get('/portal/messages/conversations');
      setConversations(r.data);
      if (r.data[0]?.current_user_id) setMyUserId(r.data[0].current_user_id);
      // my bridge user id: always the participant that is NOT marked is_teacher
      // — but simpler: find across all convs; infer from any message sender == self.
      // Portal returns my participant in all convs, so we try to resolve via any conv.
      if (!myUserId && r.data.length > 0) {
        // Heuristic: my id is the one that appears in ALL conversations
        const counts = {};
        r.data.forEach((c) => c.participants.forEach((p) => { counts[p.id] = (counts[p.id] || 0) + 1; }));
        const mine = Object.entries(counts).find(([, n]) => n === r.data.length);
        if (mine) setMyUserId(mine[0]);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nachrichten konnten nicht geladen werden');
    } finally {
      setLoadingConvs(false);
    }
  }, [myUserId]);

  useEffect(() => { reloadConvs(); }, [reloadConvs]);

  const reloadMsgs = useCallback(async (convId) => {
    if (!convId) return;
    setLoadingMsgs(true);
    try {
      const r = await api.get(`/portal/messages/conversations/${convId}/messages`, { params: { limit: 200 } });
      setMessages(r.data);
      // Infer my ID from message senders if we haven't yet
      if (!myUserId && r.data.length > 0) {
        const conv = conversations.find((c) => c.id === convId);
        if (conv) {
          // my id = participant id that sent at least one message
          const senderIds = new Set(r.data.map((m) => m.sender.id));
          const mine = conv.participants.find((p) => senderIds.has(p.id));
          if (mine) setMyUserId(mine.id);
        }
      }
      const incoming = r.data.filter((m) => m.sender.id !== myUserId && !m.deleted_at);
      await Promise.all(incoming.map(async (m) => {
        await api.post(`/portal/messages/messages/${m.id}/delivered`).catch(() => {});
        await api.post(`/portal/messages/messages/${m.id}/read`).catch(() => {});
      }));
      setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nachrichten konnten nicht geladen werden');
    } finally {
      setLoadingMsgs(false);
    }
  }, [myUserId, conversations]);

  useEffect(() => { if (activeConvId) reloadMsgs(activeConvId); }, [activeConvId, reloadMsgs]);

  const activeConv = useMemo(() => conversations.find((c) => c.id === activeConvId), [conversations, activeConvId]);

  const send = async (body, files) => {
    if (!activeConvId) return;
    if ((!body || !body.trim()) && (!files || files.length === 0)) return;
    const form = new FormData();
    form.append('body', body || '');
    (files || []).forEach((f) => form.append('attachments', f));
    try {
      await api.post(`/portal/messages/conversations/${activeConvId}/messages`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await Promise.all([reloadMsgs(activeConvId), reloadConvs()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Senden fehlgeschlagen');
    }
  };

  const deleteMsg = async (mid) => {
    if (!window.confirm('Eigene Nachricht löschen? (Inhalt wird entfernt, Audit bleibt)')) return;
    try {
      await api.delete(`/portal/messages/messages/${mid}`);
      await reloadMsgs(activeConvId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  return (
    <PortalLayout>
      <div className="space-y-4" data-testid="portal-messages-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-brand-red" /> Nachrichten
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Tausche dich mit den Lehrer:innen deiner Kinder und der Musikschul-Verwaltung aus — vertraulich & DSGVO-konform.
            </p>
          </div>
          <Button onClick={() => setNewDialogOpen(true)} className="bg-brand-red hover:bg-brand-red/90 text-white" data-testid="portal-new-msg-btn">
            <Plus className="w-4 h-4 mr-1" /> Neue Nachricht
          </Button>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden grid grid-cols-1 md:grid-cols-[320px_1fr] min-h-[70vh]">
          <aside className="border-r border-slate-200 flex flex-col min-h-0">
            <div className="p-3 border-b border-slate-200">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
                <Input placeholder="Suche" className="pl-8" disabled />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto" data-testid="portal-conv-list">
              {loadingConvs ? (
                <div className="p-6 text-slate-400 text-sm"><Loader2 className="w-4 h-4 inline mr-1 animate-spin" /> Laden…</div>
              ) : conversations.length === 0 ? (
                <div className="p-6 text-slate-400 text-sm text-center">
                  Noch keine Nachrichten. Starte mit „Neue Nachricht".
                </div>
              ) : (
                conversations.map((c) => (
                  <ConvItem key={c.id} conv={c} myId={myUserId} active={c.id === activeConvId}
                            onClick={() => setActiveConvId(c.id)} />
                ))
              )}
            </div>
          </aside>

          <section className="flex flex-col min-h-0">
            {activeConv ? (
              <>
                <header className="p-4 border-b border-slate-200">
                  <p className="font-semibold text-slate-900">
                    {activeConv.participants.filter((p) => p.id !== myUserId)
                      .map((p) => p.full_name || p.username).join(', ')}
                  </p>
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    {activeConv.participants.some((p) => p.is_teacher) && (
                      <><GraduationCap className="w-3 h-3" /> Lehrperson</>
                    )}
                  </p>
                </header>

                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="portal-thread">
                  {loadingMsgs && messages.length === 0 && (
                    <div className="text-slate-400 text-sm"><Loader2 className="w-4 h-4 inline animate-spin" /> Nachrichten werden geladen…</div>
                  )}
                  {messages.map((m) => (
                    <Bubble key={m.id} msg={m} myId={myUserId} onDelete={() => deleteMsg(m.id)} />
                  ))}
                </div>

                <Composer onSend={send} />
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                  <p>Wähle eine Nachricht links — oder starte eine neue.</p>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      {newDialogOpen && (
        <NewConvDialog
          onClose={() => setNewDialogOpen(false)}
          onCreated={(id) => { setActiveConvId(id); setNewDialogOpen(false); reloadConvs(); }}
        />
      )}
    </PortalLayout>
  );
}

function ConvItem({ conv, myId, active, onClick }) {
  const others = conv.participants.filter((p) => p.id !== myId);
  const display = others.map((p) => p.full_name || p.username).join(', ') || '—';
  const isTeacher = others.some((p) => p.is_teacher);
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b border-slate-100 ${active ? 'bg-brand-red/5' : 'hover:bg-slate-50'}`}
      data-testid={`portal-conv-${conv.id}`}
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${isTeacher ? 'bg-emerald-100 text-emerald-700' : 'bg-brand-red/10 text-brand-red'}`}>
        {isTeacher ? <GraduationCap className="w-5 h-5" /> : initials(display)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className={`text-sm font-medium truncate ${active ? 'text-brand-red' : 'text-slate-900'}`}>{display}</p>
          <span className="text-xs text-slate-400 shrink-0">{formatMessageTime(conv.last_message_at || conv.created_at)}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-slate-500 truncate">{conv.last_message_preview || <span className="italic text-slate-400">Noch keine Nachrichten</span>}</p>
          {conv.unread_count > 0 && <Badge className="bg-brand-red text-white shrink-0">{conv.unread_count}</Badge>}
        </div>
      </div>
    </button>
  );
}

function Bubble({ msg, myId, onDelete }) {
  const isOwn = msg.sender.id === myId;
  const deleted = !!msg.deleted_at;
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`} data-testid={`portal-msg-${msg.id}`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-2 shadow-sm ${isOwn ? 'bg-brand-red text-white' : 'bg-white border border-slate-200 text-slate-900'}`}>
        {!isOwn && (
          <p className="text-xs font-semibold mb-1 text-brand-red flex items-center gap-1">
            {msg.sender.is_teacher && <GraduationCap className="w-3 h-3" />}
            {msg.sender.full_name || msg.sender.username}
          </p>
        )}
        {deleted ? (
          <p className="italic opacity-70 text-sm">Nachricht gelöscht</p>
        ) : (
          <>
            {msg.body && <p className="text-sm whitespace-pre-wrap break-words">{linkify(msg.body, isOwn)}</p>}
            {msg.attachments && msg.attachments.length > 0 && (
              <div className="mt-2 space-y-1">
                {msg.attachments.map((a) => <AttRow key={a.id} att={a} light={isOwn} />)}
              </div>
            )}
          </>
        )}
        <div className={`flex items-center gap-1 mt-1 text-[10px] ${isOwn ? 'text-brand-red/70' : 'text-slate-400'}`}>
          <span>{formatMessageTime(msg.created_at)}</span>
          {isOwn && !deleted && (
            <>
              <MessageDeliveryStatus message={msg} />
              <button onClick={onDelete} className="ml-1 hover:underline" title="Nachricht löschen">
                <Trash2 className="w-3 h-3" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AttRow({ att, light }) {
  const download = async () => {
    try {
      const r = await api.get(`/portal/messages/attachments/${att.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = att.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Download fehlgeschlagen'); }
  };
  return (
    <button onClick={download} className={`flex items-center gap-2 rounded-lg px-2 py-1 text-xs border ${light ? 'bg-brand-red border-brand-red hover:bg-brand-red' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'}`}>
      <Download className="w-3 h-3" />
      <span className="truncate max-w-[180px]">{att.filename}</span>
      <span className="opacity-70">{(att.size_bytes / 1024).toFixed(0)} KB</span>
    </button>
  );
}

function Composer({ onSend }) {
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (sending) return;
    setSending(true);
    try { await onSend(body, files); setBody(''); setFiles([]); }
    finally { setSending(false); }
  };

  const addFiles = (list) => {
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    const MAX = 20 * 1024 * 1024;
    const next = [];
    for (const f of list) {
      if (!allowed.includes(f.type)) { toast.error(`Dateityp nicht erlaubt: ${f.name}`); continue; }
      if (f.size > MAX) { toast.error(`Datei zu groß (>20 MB): ${f.name}`); continue; }
      next.push(f);
    }
    setFiles((prev) => [...prev, ...next]);
  };

  return (
    <form onSubmit={submit} className="p-3 border-t border-slate-200 bg-white space-y-2">
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((f, i) => (
            <span key={i} className="inline-flex items-center gap-1 bg-brand-red/5 text-brand-red text-xs rounded-full pl-3 pr-1 py-1 border border-brand-red/20">
              <Paperclip className="w-3 h-3" /> {f.name}
              <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="hover:bg-brand-red/10 rounded-full p-0.5">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <button type="button" onClick={() => inputRef.current?.click()} className="w-9 h-9 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center shrink-0" title="Datei anhängen (PDF/JPG/PNG/WebP, max. 20 MB)">
          <Paperclip className="w-4 h-4 text-slate-600" />
        </button>
        <input ref={inputRef} type="file" hidden multiple accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => addFiles(e.target.files)} />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit(e); }}
          rows={1}
          placeholder="Nachricht eingeben…"
          className="flex-1 resize-none border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/40 max-h-40"
          data-testid="portal-composer-input"
        />
        <Button type="submit" disabled={sending || (!body.trim() && files.length === 0)} className="bg-brand-red hover:bg-brand-red/90 text-white" data-testid="portal-send-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </form>
  );
}

function NewConvDialog({ onClose, onCreated }) {
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get('/portal/messages/directory').then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return users.filter((u) => matchesFreeText(u, q));
  }, [users, q]);

  const start = async (u) => {
    setCreating(true);
    try {
      const r = await api.post('/portal/messages/conversations', { participant_ids: [u.id] });
      onCreated(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Starten fehlgeschlagen');
    } finally { setCreating(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="portal-new-dialog">
        <DialogHeader>
          <DialogTitle>Neue Nachricht</DialogTitle>
          <DialogDescription>Wähle eine:n Lehrer:in oder Mitarbeiter:in der Musikschule, um eine vertrauliche Unterhaltung zu starten.</DialogDescription>
        </DialogHeader>
        <p className="text-xs text-slate-500 mb-2">Wähle eine:n Lehrer:in oder Mitarbeiter:in der Musikschule.</p>
        <Input placeholder="Suche" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100 mt-2">
          {filtered.length === 0 && <p className="p-3 text-xs text-slate-400">Keine Treffer — vielleicht ist noch keine Lehrperson für deine Kinder zugeordnet.</p>}
          {filtered.map((u) => (
            <button key={u.id} onClick={() => start(u)} disabled={creating} className="w-full text-left px-3 py-2 flex items-center gap-2 text-sm hover:bg-slate-50" data-testid={`portal-new-pick-${u.username}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${u.is_teacher ? 'bg-emerald-100 text-emerald-700' : 'bg-brand-red/10 text-brand-red'}`}>
                {u.is_teacher ? <GraduationCap className="w-4 h-4" /> : initials(u.full_name || u.username)}
              </div>
              <div className="flex-1">
                <p className="font-medium text-slate-900">{u.full_name || u.username}</p>
                <p className="text-xs text-slate-500">{u.is_teacher ? 'Lehrperson' : 'Verwaltung'}</p>
              </div>
              <Plus className="w-4 h-4 text-brand-red" />
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
